import { navigate } from "@reach/router";
import { graphql, useStaticQuery } from "gatsby";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import ArrowRightIcon from "images/icons/arrow-right-icon.inline.svg";

import { useTheme } from "../../../context/ThemeContext";
import { shortcodePreProcessor } from "../../../utils/formatContent";
import { processForm } from "../../../utils/processForm";

const Step3 = ({ extraDetails = {} }) => {
  const {
    peopleDomain,
    planetDomain,
    currentBrand,
    localeLanguage,
    currentSiteMeta
  } = useTheme();

  const [isSticky, setIsSticky] = useState(false);
  const [programCode, setProgramCode] = useState("");
  const [formStatus, setFormStatus] = useState("pending");
  const [leadId, setLeadId] = useState("");

  const { genericDetails } = useStaticQuery(graphql`
    query {
      genericDetails: wp {
        globalProgramConfig: gviConfigSettings {
          acfTermsAndConditions {
            gblTermsAndConditions
          }
          acfGenericGlobalConfig {
            ...ProgramFeesFragment
          }
        }
      }
    }
  `);

  const programFees =
    genericDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? [];

  const gblTermsAndConditions =
    genericDetails?.globalProgramConfig?.acfTermsAndConditions
      ?.gblTermsAndConditions;

  const policyDetails = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description: gblTermsAndConditions
  });

  // console.debug(currentSiteMeta);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    const currentUrl = new URL(location);

    setProgramCode(
      extraDetails?.program_id ?? currentUrl.searchParams.get("program_id")
    ); //this param is program_id, but its actually the program code
    setLeadId(extraDetails?.lead_id ?? currentUrl.searchParams.get("lead_id"));

    const onScroll = (event) => {
      if (window.scrollY > 250) {
        setIsSticky(true);
      }
      if (window.scrollY < 250) {
        setIsSticky(false);
      }
    };

    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const onSubmit = useCallback(
    async (data) => {
      try {
        window.scrollTo(0, 0);

        const formFields = {
          form_type: "complete_application",
          privacy_policy: "privacy_policy",
          lead_id: leadId,
          program_id: programCode,
          ...data
        };

        setFormStatus("processing");

        await processForm({
          formdata: formFields,
          peopleDomain,
          planetDomain,
          currentBrand,
          localeLanguage
        });

        let forwardingStateData = {
          ...formFields
        };

        if (typeof window !== "undefined") {
          const collectHistoryData = window.history.state ?? {};
          forwardingStateData = {
            ...collectHistoryData
          };
        }

        const redirectUrl = `/thank-you-for-your-complete-application/`;
        reset();
        await navigate(redirectUrl, { state: forwardingStateData });
      } catch (err) {
        console.error("Error requestForm :: ", err);
      }

      reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentBrand,
      leadId,
      localeLanguage,
      peopleDomain,
      planetDomain,
      programCode
    ]
  );

  return (
    <div className="relative step-wrapper">
      <div className="w-full px-3 py-4 mx-auto md:px-16 sm:h-[500px] gvi-scrollbar-thin tab-content">
        <h2 className="py-6 planet:font-body font-semibold text-center text-white md:text-left font-alice">
          Application terms & conditions
        </h2>
        <div
          className="text-white terms-text tab-content_html sm:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: policyDetails }}
        />
      </div>
      {formStatus === "pending" && (
        <form
          id={"application-step-3"}
          className={`py-4 px-8 md:px-0 text-center bg-white planet:bg-gray-darkest sticky-form-terms md:py-8 ${
            isSticky ? "sticky bottom-0" : "relative"
          } transition duration-150 ease-out`}
          style={{ border: "0.5px solid #E7E7F2" }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <label
            htmlFor="tc_agree"
            className="flex items-center justify-center gap-3 text-xs text-left md:text-center text-black-lighter planet:text-white"
          >
            <input
              type="checkbox"
              className={`p-2 bg-transparent w-[18px] h-[18px]`}
              {...register("tc_agree", {
                required: {
                  value: true,
                  message: "Kindly accept the terms & conditions"
                }
              })}
              id="tc_agree"
              value={"tc_agree"}
            />
            I have read and accepted GVI&apos;s Terms & Conditions
          </label>

          {errors.tc_agree?.message && (
            <div className="text-base validation-error text-red-600 pt-4">
              {errors.tc_agree?.message}
            </div>
          )}

          <div className="flex justify-center w-full pt-8">
            <button
              type="submit"
              className="flex md:min-w-[14rem] w-full md:w-max m-auto px-8 py-2 bg-red-light planet:bg-green-standard justify-center items-center rounded-full planet:rounded-xxs"
            >
              <span className="text-base pl-2 mr-2 font-bold !text-white planet:!text-gray-darker tracking-[0.02em]">
                Complete my application
              </span>
              <ArrowRightIcon className="stroke-1 stroke-white planet:stroke-gray-darker" />
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
export default Step3;
